// pages/HomePage.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes, Global, css } from '@emotion/react';

/* ════════════════════════════════════════════════════════════════════
   DESIGN TOKENS
   ════════════════════════════════════════════════════════════════════ */
const t = {
  c: {
    primary: '#0001FC',
    primaryDeep: '#0000B8',
    primaryLight: '#3A3AFF',
    primarySoft: '#E8E8FF',
    primaryFaint: '#F4F4FF',
    gradStart: '#0001FC',
    gradMid:   '#2828D8',
    gradEnd:   '#5050E0',
    ink: '#0F1729',
    inkSoft: '#1E2A47',
    text: '#1A2332',
    textMuted: '#5C6478',
    textLight: '#8C92A3',
    border: '#E5E8EE',
    borderSoft: '#EEF0F4',
    bg: '#FAFAFB',
    bgAlt: '#F4F6FA',
    surface: '#FFFFFF',
    accent: '#FFB547',
    accentLight: '#A5A5FF',
    success: '#1FA67F',
  },
  bp: { xs: '380px', sm: '600px', md: '900px', lg: '1200px' },
};

/* ════════════════════════════════════════════════════════════════════
   GLOBAL
   ════════════════════════════════════════════════════════════════════ */
const globalStyles = css`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; }
  html {
    scroll-behavior: smooth;
    -webkit-text-size-adjust: 100%;
  }
  body {
    margin: 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    color: ${t.c.text};
    background: ${t.c.bg};
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    overflow-x: hidden;
  }
  *::selection { background: ${t.c.primary}; color: #fff; }

  /* Prevent iOS form zoom on focus by ensuring inputs are >=16px */
  input, textarea, select { font-size: 16px; }
`;

/* ════════════════════════════════════════════════════════════════════
   ANIMATIONS
   ════════════════════════════════════════════════════════════════════ */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const fadeSlideRight = keyframes`
  from { opacity: 0; transform: translateX(-24px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const fadeSlideLeft = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const pulseRing = keyframes`
  0%, 100% { transform: scale(1);    opacity: 0.45; }
  50%      { transform: scale(1.12); opacity: 0.65; }
`;
const pulseRingDelay = keyframes`
  0%, 100% { transform: scale(1);    opacity: 0.30; }
  50%      { transform: scale(1.18); opacity: 0.50; }
`;
const scrollHint = keyframes`
  0%, 100% { transform: translateY(0); opacity: 0.5; }
  50%      { transform: translateY(8px); opacity: 0.9; }
`;

/* ════════════════════════════════════════════════════════════════════
   STYLED COMPONENTS
   ════════════════════════════════════════════════════════════════════ */
const Page = styled.div`
  min-height: 100vh;
  background: ${t.c.bg};
  overflow-x: hidden;
`;

const Nav = styled.nav`
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 100;
  padding: 14px 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid ${t.c.border};
  transition: all 240ms ease;
`;

const NavInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: ${t.bp.sm}) { padding: 0 16px; }
`;

const NavBrand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: ${p => p.$onHero ? '#fff' : t.c.ink};

  .tile {
    width: 40px; height: 40px;
    background: #fff;
    border-radius: 8px;
    padding: 4px;
    display: grid; place-items: center;
    box-shadow: 0 4px 12px rgba(0, 1, 252, 0.18);
    flex-shrink: 0;
    img { width: 100%; height: 100%; object-fit: contain; }
  }

  .text-wrap { line-height: 1; }
  .name { font-weight: 700; font-size: 17px; letter-spacing: -0.01em; }
  .sub {
    display: block;
    font-size: 10.5px;
    font-weight: 500;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    opacity: 0.7;
    margin-top: 3px;
  }

  @media (max-width: ${t.bp.sm}) {
    .name { font-size: 15px; }
    .sub { display: none; }
    .tile { width: 36px; height: 36px; }
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  @media (max-width: ${t.bp.md}) { display: none; }
`;

const NavA = styled.a`
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.$onHero ? 'rgba(255,255,255,0.9)' : t.c.text};
  text-decoration: none;
  border-radius: 6px;
  transition: all 180ms;
  cursor: pointer;
  &:hover { background: ${p => p.$onHero ? 'rgba(255,255,255,0.1)' : t.c.bgAlt}; }
`;

const NavCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${p => p.$onHero ? '#fff' : t.c.primary};
  color: ${p => p.$onHero ? t.c.primary : '#fff'};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 8px;
  transition: all 180ms;
  border: 1.5px solid ${p => p.$onHero ? '#fff' : t.c.primary};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 18px rgba(0, 1, 252, 0.22);
  }

  @media (max-width: ${t.bp.sm}) { padding: 8px 14px; font-size: 13px; }
`;

const MobileBtn = styled.button`
  display: none;
  width: 44px; height: 44px;
  border: 1.5px solid ${p => p.$onHero ? 'rgba(255,255,255,0.3)' : t.c.border};
  background: ${p => p.$onHero ? 'rgba(255,255,255,0.1)' : '#fff'};
  border-radius: 8px;
  color: ${p => p.$onHero ? '#fff' : t.c.text};
  cursor: pointer;
  align-items: center; justify-content: center;
  padding: 0;
  flex-shrink: 0;
  @media (max-width: ${t.bp.md}) { display: inline-flex; }
`;

const MobileMenu = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: ${t.c.surface};
  display: flex;
  flex-direction: column;
  padding: 20px;
  transform: translateX(${p => p.$open ? '0' : '100%'});
  transition: transform 280ms cubic-bezier(0.22, 1, 0.36, 1);

  .head {
    display: flex; justify-content: space-between; align-items: center;
    padding-bottom: 16px;
    border-bottom: 1px solid ${t.c.border};
    margin-bottom: 16px;

    .close {
      width: 44px; height: 44px;
      border: 1.5px solid ${t.c.border};
      background: #fff; border-radius: 8px; cursor: pointer;
      display: grid; place-items: center;
      flex-shrink: 0;
    }
  }

  a, button {
    padding: 16px;
    font-size: 16px;
    font-weight: 500;
    color: ${t.c.text};
    text-decoration: none;
    border-bottom: 1px solid ${t.c.borderSoft};
    background: none;
    border-left: none;
    border-right: none;
    border-top: none;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
  }

  .cta {
    margin-top: auto;
    background: ${t.c.primary};
    color: #fff;
    text-align: center;
    border-radius: 10px;
    border: none;
    font-weight: 600;
    padding: 16px;
    font-size: 16px;
    text-decoration: none;
  }
`;

const Hero = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 100px 24px 80px;
  background: linear-gradient(135deg, ${t.c.gradStart} 0%, ${t.c.gradMid} 55%, ${t.c.gradEnd} 100%);
  overflow: hidden;

  @media (max-width: ${t.bp.sm}) {
    padding: 90px 16px 60px;
    min-height: auto;
  }
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.08;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  pointer-events: none;
`;

const HeroGlow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle 600px at 80% 0%, rgba(255, 255, 255, 0.18), transparent),
    radial-gradient(circle 500px at 0% 100%, rgba(0, 0, 100, 0.3), transparent);
`;

const HeroInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 48px;
  align-items: center;

  @media (max-width: ${t.bp.lg}) {
    grid-template-columns: 1fr;
    gap: 32px;
    text-align: center;
  }
`;

const HeroLeft = styled.div`
  animation: ${fadeSlideRight} 700ms ease-out both;
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
  margin-bottom: 24px;
  max-width: 100%;

  .dot { width: 6px; height: 6px; border-radius: 50%; background: ${t.c.accent}; flex-shrink: 0; }

  @media (max-width: ${t.bp.xs}) {
    font-size: 11px;
    padding: 5px 12px;
  }
`;

const HeroTitle = styled.h1`
  font-weight: 800;
  font-size: clamp(2rem, 5.5vw, 4.2rem);
  line-height: 1.08;
  letter-spacing: -0.025em;
  color: #fff;
  margin: 0 0 22px;

  .accent { color: ${t.c.accentLight}; }
`;

const HeroLede = styled.p`
  font-size: clamp(1rem, 1.4vw, 1.2rem);
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.88);
  font-weight: 400;
  margin: 0 0 36px;
  max-width: 580px;

  @media (max-width: ${t.bp.lg}) { margin-left: auto; margin-right: auto; }
`;

const HeroActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 48px;
  flex-wrap: wrap;

  @media (max-width: ${t.bp.lg}) { justify-content: center; }
  @media (max-width: ${t.bp.sm}) {
    margin-bottom: 36px;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }
`;

const HeroPrimary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 28px;
  background: #fff;
  color: ${t.c.primary};
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 10px;
  transition: all 220ms;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 14px 36px rgba(0, 0, 0, 0.25);
  }
`;

const HeroSecondary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 10px;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  transition: all 220ms;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const HeroQuickStats = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  max-width: 480px;

  @media (max-width: ${t.bp.lg}) { margin: 0 auto; }
`;

const HeroStatCard = styled.div`
  padding: 18px 20px;
  background: rgba(255, 255, 255, 0.10);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  animation: ${fadeUp} 600ms ease-out both;
  animation-delay: ${p => p.$delay || '0ms'};

  .num {
    font-size: clamp(1.6rem, 2.6vw, 2.2rem);
    font-weight: 800;
    color: ${t.c.accentLight};
    line-height: 1;
    margin-bottom: 6px;
    letter-spacing: -0.02em;
  }
  .lbl { font-size: 12.5px; font-weight: 500; color: rgba(255, 255, 255, 0.85); }

  @media (max-width: ${t.bp.sm}) {
    padding: 14px 16px;
    .num { font-size: 1.5rem; }
    .lbl { font-size: 11.5px; }
  }

  @media (max-width: ${t.bp.xs}) {
    padding: 12px 14px;
    .num { font-size: 1.3rem; }
    .lbl { font-size: 11px; }
  }
`;

const HeroRight = styled.div`
  position: relative;
  height: clamp(280px, 45vw, 480px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeSlideLeft} 800ms 200ms ease-out both;

  @media (max-width: ${t.bp.lg}) { height: 280px; }
  @media (max-width: ${t.bp.sm}) { height: 220px; }
`;

const HeroRing = styled.div`
  position: absolute;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, ${p => p.$alpha || 0.3});
  width: ${p => p.$sizeClamp || p.$size};
  height: ${p => p.$sizeClamp || p.$size};
  animation: ${p => p.$delay ? pulseRingDelay : pulseRing} 4.5s ease-in-out infinite;
  animation-delay: ${p => p.$delay || '0s'};
`;

const HeroIcon = styled.div`
  position: relative;
  z-index: 1;
  width: 130px; height: 130px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.96);
  display: grid; place-items: center;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);

  svg { width: 70px; height: 70px; color: ${t.c.primary}; }

  @media (max-width: ${t.bp.sm}) {
    width: 100px; height: 100px;
    svg { width: 54px; height: 54px; }
  }
`;

const ScrollHint = styled.div`
  position: absolute;
  bottom: 28px;
  left: 50%;
  transform: translateX(-50%);
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  animation: ${scrollHint} 2.5s ease-in-out infinite;

  @media (max-width: ${t.bp.md}) { display: none; }
`;


const Section = styled.section`
  padding: 88px 24px;
  background: ${p => p.$bg || t.c.surface};
  @media (max-width: ${t.bp.sm}) { padding: 60px 16px; }
`;

const SectionInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Eyebrow = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${t.c.primary};
  margin-bottom: 14px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(1.7rem, 3.5vw, 2.6rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${t.c.ink};
  margin: 0 0 14px;
`;

const SectionLede = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: ${t.c.textMuted};
  max-width: 620px;
  margin: 0;
`;

const SectionHead = styled.div`
  text-align: center;
  margin: 0 auto 56px;
  max-width: 700px;

  ${SectionLede} { margin: 0 auto; }
  @media (max-width: ${t.bp.sm}) { margin-bottom: 40px; }
`;

const CeoGrid = styled.div`
  display: grid;
  grid-template-columns: 5fr 7fr;
  gap: 60px;
  align-items: center;

  @media (max-width: ${t.bp.md}) {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const CeoPortrait = styled.div`
  position: relative;
  width: clamp(220px, 32vw, 360px);
  margin: 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%; left: -32px;
    transform: translateY(-50%);
    width: 8px; height: 80px;
    background: ${t.c.primary};
    border-radius: 4px;
    z-index: 0;

    @media (max-width: ${t.bp.md}) { display: none; }
  }

  .frame {
    position: relative;
    z-index: 1;
    aspect-ratio: 1 / 1;
    border-radius: 16px;
    overflow: hidden;
    background: ${t.c.bgAlt};
    border: 6px solid #fff;

    img { width: 100%; height: 100%; object-fit: cover; display: block; }
  }

  .id {
    text-align: center;
    margin-top: 24px;

    .name {
      font-size: clamp(1.1rem, 1.6vw, 1.35rem);
      font-weight: 700;
      color: ${t.c.ink};
      margin-bottom: 4px;
    }
    .role { font-size: 13px; color: ${t.c.textMuted}; font-weight: 500; }
    .signature {
      display: inline-block;
      margin-top: 14px;
      padding: 4px 12px;
      background: ${t.c.primaryFaint};
      color: ${t.c.primary};
      border-radius: 6px;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }
  }
`;

const CeoBody = styled.div`
  h2 {
    font-size: clamp(1.5rem, 3vw, 2.3rem);
    font-weight: 800;
    color: ${t.c.ink};
    line-height: 1.15;
    letter-spacing: -0.02em;
    margin: 0 0 22px;
  }

  p {
    font-size: 16px;
    line-height: 1.75;
    color: ${t.c.text};
    margin: 0 0 16px;
    &:last-of-type { margin-bottom: 28px; }
  }

  .welcome-message {
    color: ${t.c.primary};
    font-weight: 700;
  }

  .pull {
    border-left: 3px solid ${t.c.primary};
    padding: 4px 0 4px 18px;
    margin: 24px 0;
    font-size: 17px;
    font-weight: 500;
    color: ${t.c.ink};
    font-style: italic;
    line-height: 1.55;
  }

  .cta-row {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;

    @media (max-width: ${t.bp.sm}) {
      flex-direction: column;
      align-items: stretch;
    }
  }

  @media (max-width: ${t.bp.md}) {
    text-align: center;
    .pull { text-align: left; }
    .cta-row { justify-content: center; }
  }
`;

const CeoCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 26px;
  background: ${t.c.primary};
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 8px;
  border: 1.5px solid ${t.c.primary};
  transition: all 200ms;

  &:hover {
    background: ${t.c.primaryDeep};
    border-color: ${t.c.primaryDeep};
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(0, 1, 252, 0.25);
  }
`;

const CeoCtaGhost = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  background: transparent;
  color: ${t.c.primary};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 8px;
  border: 1.5px solid ${t.c.border};
  transition: all 200ms;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    border-color: ${t.c.primary};
    background: ${t.c.primaryFaint};
  }
`;

const ShowcaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: ${t.bp.md}) { grid-template-columns: 1fr; gap: 18px; }
`;

const ShowcaseCard = styled.div`
  background: ${t.c.surface};
  border: 1px solid ${t.c.border};
  border-radius: 16px;
  overflow: hidden;
  transition: all 320ms ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 40px rgba(15, 23, 41, 0.08);
    border-color: ${t.c.primarySoft};
    .head { transform: scale(1.02); }
    .arrow { transform: translateX(4px); color: ${t.c.primary}; }
  }

  .head {
    aspect-ratio: 16 / 9;
    background: ${p => p.$bg};
    display: grid; place-items: center;
    position: relative;
    overflow: hidden;
    transition: transform 320ms ease;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: radial-gradient(circle at center, rgba(255,255,255,0.16), transparent 70%);
    }
  }

  .head-icon {
    width: 76px; height: 76px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 16px;
    display: grid; place-items: center;
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
    border: 3px solid rgba(255, 255, 255, 0.4);
    position: relative;
    z-index: 1;

    img { width: 46px; height: 46px; object-fit: contain; }
  }

  .badge {
    position: absolute;
    top: 14px; right: 14px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 600;
    color: ${t.c.ink};
    z-index: 2;
    letter-spacing: 0.04em;
  }

  .body {
    padding: 24px;
    display: flex;
    flex-direction: column;
    flex: 1;

    h3 {
      font-size: 17px;
      font-weight: 700;
      color: ${t.c.ink};
      margin: 0 0 10px;
      line-height: 1.3;
      letter-spacing: -0.01em;
    }

    p {
      font-size: 14px;
      color: ${t.c.textMuted};
      line-height: 1.55;
      margin: 0 0 16px;
      flex: 1;
    }

    .foot {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding-top: 14px;
      border-top: 1px solid ${t.c.borderSoft};

      .meta { font-size: 12px; color: ${t.c.textLight}; font-weight: 500; }
      .arrow {
        font-size: 13px;
        font-weight: 600;
        color: ${t.c.textMuted};
        transition: all 220ms;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
    }
  }
`;

const SeeAllBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 48px auto 0;
  padding: 14px 30px;
  background: transparent;
  color: ${t.c.primary};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  border-radius: 10px;
  border: 1.5px solid ${t.c.primary};
  transition: all 200ms;

  &:hover {
    background: ${t.c.primary};
    color: #fff;
  }
`;

const SeeAllWrap = styled.div`
  text-align: center;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;

  @media (max-width: ${t.bp.md}) { grid-template-columns: repeat(2, 1fr); gap: 20px; }
  @media (max-width: ${t.bp.sm}) { grid-template-columns: 1fr; }
`;

const Feature = styled.div`
  text-align: center;
  padding: 28px 20px;
  border-radius: 12px;
  transition: all 280ms ease;
  cursor: pointer;

  &:hover {
    background: ${t.c.bgAlt};
    .ico {
      background: ${t.c.primary};
      transform: rotate(5deg) scale(1.05);
      svg { color: #fff; }
    }
  }

  .ico {
    width: 64px; height: 64px;
    border-radius: 14px;
    background: ${t.c.primaryFaint};
    display: grid; place-items: center;
    margin: 0 auto 18px;
    transition: all 280ms ease;

    svg { width: 28px; height: 28px; color: ${t.c.primary}; transition: color 280ms; }
  }

  h3 {
    font-size: 17px;
    font-weight: 700;
    color: ${t.c.ink};
    margin: 0 0 10px;
    letter-spacing: -0.01em;
  }
  p {
    font-size: 14px;
    line-height: 1.6;
    color: ${t.c.textMuted};
    margin: 0;
  }
`;

const FinalBand = styled.section`
  position: relative;
  padding: 80px 24px;
  overflow: hidden;
  background: linear-gradient(135deg, ${t.c.gradStart} 0%, ${t.c.gradMid} 60%, ${t.c.gradEnd} 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.06'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    pointer-events: none;
  }

  @media (max-width: ${t.bp.sm}) { padding: 60px 16px; }
`;

const FinalInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  text-align: center;
  color: #fff;

  h2 {
    font-size: clamp(1.8rem, 4vw, 2.8rem);
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.025em;
    margin: 0 0 16px;
  }
  p {
    font-size: 16px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.88);
    max-width: 580px;
    margin: 0 auto 32px;
  }

  .actions {
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const Footer = styled.footer`
  background: ${t.c.ink};
  color: rgba(255, 255, 255, 0.7);
  padding: 48px 24px 28px;
`;

const FooterInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 40px;
  padding-bottom: 32px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 24px;

  @media (max-width: ${t.bp.md}) {
    grid-template-columns: 1fr 1fr;
    gap: 28px;
  }
`;

const FooterBrand = styled.div`
  .top {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 14px;

    .tile {
      width: 36px; height: 36px;
      background: #fff;
      border-radius: 6px;
      padding: 4px;
      display: grid; place-items: center;
      img { width: 100%; height: 100%; object-fit: contain; }
    }
    .name { font-size: 16px; font-weight: 700; color: #fff; }
  }
  p {
    font-size: 13px;
    line-height: 1.6;
    margin: 0;
    max-width: 320px;
  }
`;

const FooterCol = styled.div`
  h4 {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    margin: 0 0 14px;
    letter-spacing: 0.04em;
  }
  a, button {
    display: block;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.65);
    text-decoration: none;
    padding: 4px 0;
    transition: color 180ms;
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    &:hover { color: ${t.c.accentLight}; }
  }
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  flex-wrap: wrap;
  gap: 12px;

  .legal a {
    color: rgba(255, 255, 255, 0.5);
    text-decoration: none;
    margin-left: 16px;
    &:hover { color: #fff; }
  }
`;

/* ════════════════════════════════════════════════════════════════════
   ICONS
   ════════════════════════════════════════════════════════════════════ */
const Ico = {
  Arrow: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  Play: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="5 3 19 12 5 21" /></svg>
  ),
  Brain: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5a3 3 0 1 0-5.997.142 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 19v-7" />
      <path d="M12 5a3 3 0 1 1 5.997.142 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 19" />
    </svg>
  ),
  Shield: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" />
    </svg>
  ),
  Chart: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  Globe: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  Users: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  Heart: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  ),
  Bolt: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10" />
    </svg>
  ),
  Activity: () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  Hero: () => (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  Menu: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
  Close: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  ),
  Sparkle: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2 L14 9 L21 11 L14 13 L12 20 L10 13 L3 11 L10 9 Z" /></svg>
  ),
  Down: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
};

/* ════════════════════════════════════════════════════════════════════
   COMPONENT
   ════════════════════════════════════════════════════════════════════ */
const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
  }, [menuOpen]);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const showcase = [
    {
      icon: <img src="/logo192.png" alt="Visioning Assessment logo" />,
      title: 'DASS-21 Mental Health Screening',
      desc: 'Validated 21-item screening for depression, anxiety, and stress severity. Auto-scored with clinical interpretation.',
      meta: '21 items · 10-15 min',
      badge: 'Most used',
      bg: 'linear-gradient(135deg, #0001FC, #2828D8)',
    },
    {
      icon: <img src="/logo192.png" alt="Visioning Assessment logo" />,
      title: 'PHQ-9 Depression Screening',
      desc: 'Patient Health Questionnaire — A standard tool for diagnosing and tracking depressive disorders in primary care.',
      meta: '9 items · 5-7 min',
      bg: 'linear-gradient(135deg, #0001FC, #2828D8)',
    },
    {
      icon: <img src="/logo192.png" alt="Visioning Assessment logo" />,
      title: 'GAD-7 Anxiety Assessment',
      desc: 'Generalized Anxiety Disorder screener with established clinical thresholds and severity classification.',
      meta: '7 items · 4-6 min',
      bg: 'linear-gradient(135deg, #0001FC, #2828D8)',
    },
  ];

  const features = [
    { ico: <Ico.Shield />, title: 'HIPAA-Ready Security',          desc: 'Enterprise-grade encryption and compliance controls protect every client interaction and data point.' },
    { ico: <Ico.Bolt />,   title: 'Instant Scoring',                desc: 'Automated scoring delivers results in under a second — no manual calculation, no spreadsheet exports.' },
    { ico: <Ico.Chart />,  title: 'Rich Reporting',                 desc: 'Generate publication-quality PDF reports with visual subscale breakdowns and clinical interpretations.' },
    { ico: <Ico.Users />,  title: 'Client Management',              desc: 'Organize clients, track progress over time, and manage assessment schedules from one dashboard.' },
    { ico: <Ico.Globe />,  title: 'Cloud Access Anywhere',          desc: 'Work from desktop, tablet, or phone. Your practice goes wherever your clients need you.' },
    { ico: <Ico.Brain />,  title: 'Validated Instrument Library',   desc: 'A curated, peer-reviewed library of psychological assessments — added to and updated regularly.' },
  ];

  return (
    <Page>
      <Global styles={globalStyles} />

      {/* ───── NAV ───── */}
      <Nav $scrolled={scrolled}>
        <NavInner>
          <NavBrand to="/" $onHero={false}>
            <span className="tile">
              <img src="/logo192.png" alt="Visioning Assessment" />
            </span>
            <div className="text-wrap">
              <div className="name">Visioning Assessment</div>
              <span className="sub">Practitioner Portal</span>
            </div>
          </NavBrand>

          <NavLinks>
            <NavA $onHero={false} onClick={scrollTo('about')}>About</NavA>
            <NavA $onHero={false} onClick={scrollTo('assessments')}>Assessments</NavA>
            <NavA $onHero={false} onClick={scrollTo('features')}>Features</NavA>
            <NavA as={Link} to="/login" $onHero={false} style={{textDecoration:'none'}}>Sign in</NavA>
            <NavCTA to="/register" $onHero={false}>
              Get Started
              <Ico.Arrow />
            </NavCTA>
          </NavLinks>

          <MobileBtn onClick={() => setMenuOpen(true)} $onHero={false} aria-label="Open menu">
            <Ico.Menu />
          </MobileBtn>
        </NavInner>
      </Nav>

      {/* ───── MOBILE MENU ───── */}
      <MobileMenu $open={menuOpen}>
        <div className="head">
          <NavBrand to="/">
            <span className="tile">
              <img src="/logo192.png" alt="" />
            </span>
            <div className="text-wrap">
              <div className="name">Visioning</div>
            </div>
          </NavBrand>
          <button className="close" onClick={() => setMenuOpen(false)} aria-label="Close menu"><Ico.Close /></button>
        </div>
        <button onClick={scrollTo('about')}>About</button>
        <button onClick={scrollTo('assessments')}>Assessments</button>
        <button onClick={scrollTo('features')}>Features</button>
        <Link to="/login" onClick={() => setMenuOpen(false)} style={{padding:'16px',fontSize:'16px',fontWeight:500,color:t.c.text,textDecoration:'none',borderBottom:`1px solid ${t.c.borderSoft}`}}>Sign in</Link>
        <Link to="/register" onClick={() => setMenuOpen(false)} className="cta">
          Get Started
        </Link>
      </MobileMenu>

      {/* ───── HERO ───── */}
      <Hero>
        <HeroPattern />
        <HeroGlow />

        <HeroInner>
          <HeroLeft>
            <HeroBadge>
              <span className="dot" />
              <Ico.Sparkle />
              AI Powered Tool
            </HeroBadge>

            <HeroTitle>
              Professional Psychological<br />
              Assessment <span className="accent">Platform</span>
            </HeroTitle>

            <HeroLede>
              Deliver validated assessments, generate clinical reports, and grow
              your practice with tools built specifically for mental health
              professionals.
            </HeroLede>

            <HeroActions>
              <HeroPrimary to="/register">
                Get Started Free
                <Ico.Arrow />
              </HeroPrimary>
              <HeroSecondary onClick={scrollTo('about')}>
                <Ico.Play />
                Learn More
              </HeroSecondary>
            </HeroActions>

            <HeroQuickStats>
              <HeroStatCard $delay="500ms">
                <div className="num">100+</div>
                <div className="lbl">Validated Assessments</div>
              </HeroStatCard>
              <HeroStatCard $delay="650ms">
                <div className="num">24/7</div>
                <div className="lbl">Availability</div>
              </HeroStatCard>
            </HeroQuickStats>
          </HeroLeft>

          <HeroRight>
            <HeroRing $sizeClamp="clamp(220px, 60vw, 380px)" $alpha={0.25} />
            <HeroRing $sizeClamp="clamp(160px, 45vw, 280px)" $alpha={0.35} $delay="1s" />
            <HeroIcon>
              <Ico.Hero />
            </HeroIcon>
          </HeroRight>
        </HeroInner>

        <ScrollHint>
          <span>Scroll to explore</span>
          <Ico.Down />
        </ScrollHint>
      </Hero>

      {/* ───── CEO MESSAGE ───── */}
      <Section id="about">
        <SectionInner>
          <CeoGrid>
            <CeoPortrait>
              <div className="frame">
                <img src="/images/ceo.jpg" alt="CEO of Visioning Assessment" />
              </div>
              <div className="id">
                <div className="name">Dr. Francis Agyei</div>
                <div className="role">Founder &amp; Chief Executive Officer</div>
                <div className="signature">A Message from the CEO</div>
              </div>
            </CeoPortrait>

            <CeoBody>
              <p className="welcome-message">Welcome to Visioning Assessment — Africa’s leading Digital Mental Health Assessment Ecosystem.</p>
              <p>
                At Visioning Assessment, we believe the future of mental health care in Africa depends on more than
                individual practitioners working in isolation. It requires intelligent, scalable, and culturally responsive
                systems that empower professionals, strengthen collaboration, standardize care, and expand access to quality
                psychological services across the continent.
              </p>
              <p>Visioning Assessment was created to become that infrastructure.</p>
              <p>
                Our platform combines digital psychological assessments, automated reporting, behavioral analytics,
                practitioner networking, and professional growth systems into one integrated ecosystem infrastructure
                designed specifically for African clinical, educational, organizational, and community contexts. By reducing
                administrative burden and enhancing assessment efficiency, we enable practitioners to focus more on what
                matters most - delivering meaningful, timely and evidence-based care.
              </p>
              <p>
                But we are building more than technology. We are building a continental community of practice that advances
                clinical excellence, innovation, professional visibility, research collaboration, and sustainable
                practitioner growth.
              </p>
              <p>
                Together, we are shaping the future of digital psychological assessment and mental health practice in Africa.
              </p>
              <p>Thank you for becoming part of this vision.</p>
              <div className="pull">— Founder &amp; CEO</div>
              <p><strong>Vision</strong></p>
              <p>
                To build Africa’s leading digital mental health assessment ecosystem for scalable, culturally responsive, and
                data-driven psychological care.
              </p>
              <p><strong>Mission</strong></p>
              <p>
                To empower mental health practitioners across Africa through intelligent assessment infrastructure,
                professional collaboration, innovation, and standardized systems that advance practitioner growth, clinical
                excellence, and access to quality mental health care.
              </p>

              <div className="cta-row">
                <CeoCta to="/register">
                  Join the Platform
                  <Ico.Arrow />
                </CeoCta>
                <CeoCtaGhost onClick={scrollTo('features')}>
                  Explore Features
                </CeoCtaGhost>
              </div>
            </CeoBody>
          </CeoGrid>
        </SectionInner>
      </Section>

      {/* ───── SHOWCASE ───── */}
      <Section $bg={t.c.bgAlt} id="assessments">
        <SectionInner>
          <SectionHead>
            <Eyebrow>Featured Assessments</Eyebrow>
            <SectionTitle>Validated tools, instantly available</SectionTitle>
            <SectionLede>
              Every assessment in our library is peer-reviewed, clinically
              validated, and ready to deploy to your clients in seconds.
            </SectionLede>
          </SectionHead>

          <ShowcaseGrid>
            {showcase.map((item, i) => (
              <ShowcaseCard key={i} $bg={item.bg}>
                <div className="head">
                  {item.badge && <span className="badge">{item.badge}</span>}
                  <div className="head-icon">{item.icon}</div>
                </div>
                <div className="body">
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                  <div className="foot">
                    <span className="meta">{item.meta}</span>
                    <Link to="/register" className="arrow">View Details <Ico.Arrow /></Link>
                  </div>
                </div>
              </ShowcaseCard>
            ))}
          </ShowcaseGrid>

          <SeeAllWrap>
            <SeeAllBtn to="/register">
              Browse All Assessments
              <Ico.Arrow />
            </SeeAllBtn>
          </SeeAllWrap>
        </SectionInner>
      </Section>

      {/* ───── FEATURES ───── */}
      <Section id="features">
        <SectionInner>
          <SectionHead>
            <Eyebrow>Why Choose Us</Eyebrow>
            <SectionTitle>Excellence in psychological practice</SectionTitle>
            <SectionLede>
              A comprehensive platform built around the workflow practitioners
              actually use. Right from intake data through to final report.
            </SectionLede>
          </SectionHead>

          <FeaturesGrid>
            {features.map((f, i) => (
              <Feature key={i}>
                <div className="ico">{f.ico}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </Feature>
            ))}
          </FeaturesGrid>
        </SectionInner>
      </Section>

      {/* ───── FINAL CTA ───── */}
      <FinalBand>
        <FinalInner>
          <h2>Ready to transform your practice?</h2>
          <p>
            Join thousands of practitioners who have streamlined their
            assessment workflow and reclaimed hours of their week.
          </p>
          <div className="actions">
            <HeroPrimary to="/register">
              Start Free Trial
              <Ico.Arrow />
            </HeroPrimary>
            <HeroSecondary onClick={scrollTo('about')}>
              Learn More
            </HeroSecondary>
          </div>
        </FinalInner>
      </FinalBand>

      {/* ───── FOOTER ───── */}
      <Footer>
        <FooterInner>
          <FooterTop>
            <FooterBrand>
              <div className="top">
                <span className="tile">
                  <img src="/logo192.png" alt="" />
                </span>
                <span className="name">Visioning Assessment</span>
              </div>
              <p>Professional psychological assessment platform built for practitioners. Validated instruments, instant scoring, clinical reports.</p>
            </FooterBrand>

            <FooterCol>
              <h4>PLATFORM</h4>
              <button onClick={scrollTo('assessments')}>Assessments</button>
              <button onClick={scrollTo('features')}>Features</button>
            </FooterCol>

            <FooterCol>
              <h4>ACCOUNT</h4>
              <Link to="/login">Sign In</Link>
              <Link to="/register">Register</Link>
            </FooterCol>
          </FooterTop>

          <FooterBottom>
            <span>© {new Date().getFullYear()} Visioning Assessment. All rights reserved.</span>
            <span className="legal">
              <a href="#">Privacy</a>
              <a href="#">Terms</a>
              <a href="#">Security</a>
            </span>
          </FooterBottom>
        </FooterInner>
      </Footer>
    </Page>
  );
};

export default HomePage;
